/**
 * B1084 (M24 leg 2) — the buyer flow as WebMCP tools.
 *
 * Meta Muse and the browser agents buy by driving the merchant's own page.
 * Without declared tools they guess at buttons; with them they call the same
 * steps a buyer clicks. A browser that offers `navigator.modelContext` gets
 * the tools; any other browser gets nothing and the page is unchanged.
 *
 * Slice (a) declares the flow's first steps — what the business sells, open
 * one, its showtimes — answered from the catalog the page draws (the JSON-LD's
 * own source, ./jsonLd.ts), never a second read. Opening a card is the click,
 * not a shortcut past it. Every refusal names the call that fixes it, the way
 * a field's hint names the fix.
 *
 * Pure apart from the registration seam: no fetch, no React.
 */
import { majorUnits, type JsonLdCard, type JsonLdSlot } from "./jsonLd.js";

/**
 * MCP's own CallToolResult shape. `isError` is the protocol's boolean flag
 * (the agent reads it; the refusal's words ride `content`), not a per-read
 * refusal field a screen owes a seat (B317's subject is UI state).
 */
export type WebMcpResult = Readonly<{
  content: readonly { readonly type: "text"; readonly text: string }[];
  isError?: boolean;
}>;

export interface WebMcpTool {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: { readonly [key: string]: unknown };
  execute(input: unknown): Promise<WebMcpResult>;
}

export interface BuyerToolDeps {
  /** The catalog the page draws; empty until it has answered. */
  cards(): readonly JsonLdCard[];
  /** Open a card's page — the same path a click on the card takes. */
  open(productId: string): void;
  /** The card's showtimes from its calendar answer; undefined until its page has read it. */
  showtimes(productId: string): readonly JsonLdSlot[] | undefined;
}

/** The flow's steps, in the order a buyer takes them. */
export const BUYER_TOOL_NAMES = ["list_offerings", "open_offering", "list_showtimes"] as const;

const said = (text: string): WebMcpResult => ({ content: [{ type: "text", text }] });
const refused = (text: string): WebMcpResult => ({ content: [{ type: "text", text }], isError: true });
const json = (value: unknown): WebMcpResult => said(JSON.stringify(value));

const PRODUCT_INPUT = {
  type: "object",
  properties: { productId: { type: "string", description: "An id from list_offerings." } },
  required: ["productId"],
  additionalProperties: false,
} as const;

function productIdOf(input: unknown): string | undefined {
  const id = typeof input === "object" && input !== null ? (input as { productId?: unknown }).productId : undefined;
  return typeof id === "string" && id.trim() !== "" ? id.trim() : undefined;
}

export function buyerTools(deps: BuyerToolDeps): readonly WebMcpTool[] {
  const find = (input: unknown): { card: JsonLdCard } | { refusal: WebMcpResult } => {
    const id = productIdOf(input);
    if (id === undefined) return { refusal: refused("Say which one with a productId — call list_offerings for the ids this page sells.") };
    const card = deps.cards().find((c) => c.productId === id);
    if (card === undefined) return { refusal: refused(`Nothing on this page has the id “${id}” — call list_offerings for the ids it sells.`) };
    return { card };
  };

  return [
    {
      name: "list_offerings",
      description: "List what this business sells on this page: each offering's productId, name, summary and ticket types with their prices.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      execute: async () => {
        const cards = deps.cards();
        if (cards.length === 0) return refused("This page's catalog is still loading — ask again in a moment.");
        return json({
          offerings: cards.map((c) => ({
            productId: c.productId,
            name: c.name,
            ...(c.presentation?.summary !== undefined ? { summary: c.presentation.summary } : {}),
            ticketTypes: c.variants
              .filter((v) => v.displayPrice !== undefined)
              .map((v) => ({ name: v.name, price: majorUnits(v.displayPrice!.amount), currency: v.displayPrice!.currency })),
          })),
        });
      },
    },
    {
      name: "open_offering",
      description: "Open one offering's page, the way a buyer clicks its card: its times and ticket choices load there.",
      inputSchema: PRODUCT_INPUT,
      execute: async (input) => {
        const found = find(input);
        if ("refusal" in found) return found.refusal;
        deps.open(found.card.productId);
        return said(`Opened ${found.card.name}. Call list_showtimes with “${found.card.productId}” for its times.`);
      },
    },
    {
      name: "list_showtimes",
      description: "List the times on sale for an opened offering: each slotId, its start time (UTC) and its from-price.",
      inputSchema: PRODUCT_INPUT,
      execute: async (input) => {
        const found = find(input);
        if ("refusal" in found) return found.refusal;
        const { card } = found;
        const times = deps.showtimes(card.productId);
        if (times === undefined) return refused(`${card.name}'s times are read when its page opens — call open_offering with “${card.productId}”, then ask again.`);
        if (times.length === 0) return said(`${card.name} has no times on sale right now.`);
        return json({
          productId: card.productId,
          showtimes: times.map((s) => ({
            slotId: s.slotId,
            start: new Date(s.start).toISOString(),
            ...(s.from !== undefined ? { from: { price: majorUnits(s.from.amount), currency: s.from.currency } } : {}),
          })),
        });
      },
    },
  ];
}

/** The browser's WebMCP surface — the two shapes the draft has carried. */
export interface ModelContextLike {
  registerTool?(tool: WebMcpTool): { unregister(): void } | void;
  unregisterTool?(name: string): void;
  provideContext?(context: { readonly tools: readonly WebMcpTool[] }): void;
  clearContext?(): void;
}

export function modelContextOf(nav: unknown): ModelContextLike | undefined {
  const mc = typeof nav === "object" && nav !== null ? (nav as { modelContext?: unknown }).modelContext : undefined;
  if (typeof mc !== "object" || mc === null) return undefined;
  const m = mc as ModelContextLike;
  return typeof m.registerTool === "function" || typeof m.provideContext === "function" ? m : undefined;
}

/**
 * Register the tools; the returned cleanup takes them back. A browser that
 * refuses (a permission, a draft that moved) leaves the page as it was —
 * the tools are an extra door, never a reason for the page to fail.
 */
export function registerBuyerTools(mc: ModelContextLike | undefined, tools: readonly WebMcpTool[]): () => void {
  if (mc === undefined) return () => undefined;
  try {
    if (typeof mc.registerTool === "function") {
      const undo = tools.map((tool) => {
        const handle = mc.registerTool!(tool);
        return () => (handle !== undefined ? handle.unregister() : mc.unregisterTool?.(tool.name));
      });
      return () => undo.forEach((u) => quietly(u));
    }
    mc.provideContext?.({ tools });
    return () => quietly(() => mc.clearContext?.());
  } catch {
    return () => undefined;
  }
}

function quietly(fn: () => void): void {
  try {
    fn();
  } catch {
    /* the page outlives a browser that refused */
  }
}
